import React from "react"
import Header from "../Header"
import Footer from "../Footer"
import WhatsAppFloat from "../WhatsAppFloat"
import { FileText, Shield, Users, Lock, AlertTriangle, Settings, Mail, Globe, Building, Scale } from "lucide-react"
import { useTranslation } from "react-i18next"

export default function Terms() {
  const { t } = useTranslation('terms');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-black">
      <Header />

      <main className="pt-28 md:pt-32 pb-16">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {t('title')}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('subtitle')}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span>{t('effectiveDate')}</span>
              </div>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8 shadow-sm">
              <p className="text-lg text-gray-800 leading-relaxed">{t('intro')}</p>
            </div>

            {/* Section 1 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section1.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section1.text')}</p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section2.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section2.text')}</p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section3.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800 mb-4">{t('section3.intro')}</p>
                <ul className="space-y-2 text-gray-800">
                  {t('section3.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">&bull;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section4.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section4.text')}</p>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section5.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800 mb-4">{t('section5.intro')}</p>
                <ul className="space-y-2 text-gray-800">
                  {t('section5.items', { returnObjects: true }).map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-600 font-bold">&bull;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-gray-800 mt-4">{t('section5.note')}</p>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section6.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section6.text')}</p>
              </div>
            </section>

            {/* Section 7 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section7.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section7.text')}</p>
              </div>
            </section>

            {/* Section 8 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section8.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section8.text')}</p>
              </div>
            </section>

            {/* Section 9 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section9.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800">{t('section9.text')}</p>
              </div>
            </section>

            {/* Section 10 */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('section10.title')}</h2>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <p className="text-gray-800 mb-4">{t('section10.intro')}</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium">support@trackteam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <Globe className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">https://www.trackteam.app</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Building className="w-5 h-5 text-purple-600" />
                    <span className="text-purple-800 font-medium">{t('section10.company')}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer Note */}
            <div className="text-center mt-12 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <p className="text-gray-600 text-sm">{t('footerNote')}</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  )
}
